import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { attachForegroundFcm, ensureWebFcmRegistered } from "../services/firebaseMessagingService";
import { handleFcmNotificationMessage } from "../services/fcmNotificationHandler";
import { syncNotificationBadgeFromServer } from "../utils/notificationSync";

const isSignedIn = () => {
  const t = localStorage.getItem("token") || sessionStorage.getItem("token");
  return !!(t && t !== "FIRST_TIME_LOGIN" && String(t).trim() !== "");
};

/**
 * Subscribes to foreground FCM, service-worker notification clicks, and syncs topics like Flutter.
 */
export default function FcmNotificationListener() {
  const navigate = useNavigate();

  useEffect(() => {
    let detach = () => {};
    let pollTimer = null;
    let lastNotificationCount = null;
    let pollingStopped = false;

    (async () => {
      if (isSignedIn()) {
        await ensureWebFcmRegistered();
        await syncNotificationBadgeFromServer();
      }
      detach = await attachForegroundFcm(navigate, isSignedIn);
    })();

    const onSwMessage = (event) => {
      if (!isSignedIn()) return;
      const msg = event?.data;
      if (!msg || msg.type !== "MFG_FCM_ACTION" || !msg.payload) return;
      handleFcmNotificationMessage(navigate, msg.payload, false);
    };

    const consumeInitialSwPayload = () => {
      if (!isSignedIn()) return;
      try {
        const url = new URL(window.location.href);
        const encoded = url.searchParams.get("mfg_fcm_payload");
        if (!encoded) return;
        let payload = null;
        try {
          payload = JSON.parse(decodeURIComponent(encoded));
        } catch (_) {
          payload = null;
        }
        if (payload) {
          handleFcmNotificationMessage(navigate, payload, false);
        }
        url.searchParams.delete("mfg_fcm_payload");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      } catch (_) {}
    };

    try {
      navigator.serviceWorker?.addEventListener?.("message", onSwMessage);
    } catch (_) {}
    consumeInitialSwPayload();

    const syncNotificationBadge = async () => {
      if (pollingStopped || !isSignedIn()) return;
      try {
        const result = await syncNotificationBadgeFromServer();
        if (!result) return;
        const count = result.count;

        if (lastNotificationCount == null) {
          lastNotificationCount = count;
          return;
        }

        if (count > lastNotificationCount) {
          try {
            window.dispatchEvent(
              new CustomEvent("mfg:fcm-foreground-toast", {
                detail: {
                  title: "Notification",
                  body: "You have a new notification.",
                },
              })
            );
          } catch (_) {}
        }
        lastNotificationCount = count;
      } catch (_) {}
    };

    syncNotificationBadge();
    pollTimer = window.setInterval(syncNotificationBadge, 15000);

    return () => {
      pollingStopped = true;
      if (pollTimer) window.clearInterval(pollTimer);
      if (typeof detach === "function") detach();
      try {
        navigator.serviceWorker?.removeEventListener?.("message", onSwMessage);
      } catch (_) {}
    };
  }, [navigate]);

  return null;
}
